import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, Paperclip, Sparkles } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
interface ExpertFinderModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function ExpertFinderModal({
  isOpen,
  onClose
}: ExpertFinderModalProps) {
  const [step, setStep] = useState<'query' | 'results' | 'ask'>('query');
  const [query, setQuery] = useState('');
  const [selectedExpert, setSelectedExpert] = useState<{ name: string; role: string; match: string; evidence: string } | null>(null);
  const handleSearch = () => {
    if (query.trim()) setStep('results');
  };
  const handleAsk = (expert: { name: string; role: string; match: string; evidence: string }) => {
    setSelectedExpert(expert);
    setStep('ask');
  };
  return <AnimatePresence>
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />

          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-xl bg-surface-900 border border-surface-600 rounded-xl shadow-2xl z-50 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-surface-600">
              <h2 className="text-lg font-semibold text-text-primary">
                {step === 'query' && 'Find an Expert'}
                {step === 'results' && 'Expert Results'}
                {step === 'ask' && `Ask ${selectedExpert?.name}`}
              </h2>
              <button onClick={onClose} className="text-text-muted hover:text-text-primary">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Step 1: Query Input */}
            {step === 'query' && <div className="p-8">
                <h3 className="text-2xl font-medium text-text-primary mb-6 text-center">
                  Who knows about...
                </h3>
                <div className="relative mb-8">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
                  <input type="text" value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()} placeholder="e.g., DNS caching, Kubernetes, Onboarding..." className="w-full bg-surface-800 border border-surface-500 rounded-xl py-4 pl-12 pr-4 text-lg text-text-primary placeholder-text-muted focus:outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 transition-all" autoFocus />
                </div>

                <div className="flex flex-wrap gap-2 justify-center">
                  {['Redis Migration', 'API Security', 'Mobile Auth'].map(topic => <button key={topic} onClick={() => {
              setQuery(topic);
              setStep('results');
            }} className="px-3 py-1.5 rounded-full bg-surface-800 hover:bg-surface-700 text-sm text-text-secondary hover:text-text-primary transition-colors border border-surface-500">
                        {topic}
                      </button>)}
                </div>
              </div>}

            {/* Step 2: Results */}
            {step === 'results' && <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-text-secondary">
                    Found 3 experts for "{query}"
                  </span>
                  <button onClick={() => setStep('query')} className="text-sm text-teal-400 hover:text-teal-300">
                    New Search
                  </button>
                </div>

                {[{
            name: 'Sarah Chen',
            role: 'Staff Engineer',
            match: 'High',
            evidence: 'Completed 3 streams on this topic'
          }, {
            name: 'Mike Ross',
            role: 'Product Lead',
            match: 'Medium',
            evidence: 'Active stream: DNS Optimization'
          }, {
            name: 'Alex M.',
            role: 'DevOps',
            match: 'Medium',
            evidence: 'Referenced in 12 transcripts'
          }].map((expert, i) => <div key={i} className="p-4 rounded-xl bg-surface-800/50 border border-surface-500 hover:border-surface-500 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-text-primary font-medium">
                          {expert.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div>
                          <div className="font-medium text-text-primary">
                            {expert.name}
                          </div>
                          <div className="text-xs text-text-secondary">
                            {expert.role}
                          </div>
                        </div>
                      </div>
                      <Badge variant={expert.match === 'High' ? 'success' : 'secondary'}>
                        {expert.match} Match
                      </Badge>
                    </div>

                    <div className="flex items-center gap-2 mb-4 text-xs text-text-secondary bg-surface-900/50 p-2 rounded-lg">
                      <Sparkles className="w-3 h-3 text-teal-400" />
                      {expert.evidence}
                    </div>

                    <div className="flex gap-2">
                      <Button variant="secondary" className="flex-1" size="sm">
                        View Streams
                      </Button>
                      <Button className="flex-1 bg-teal-600 hover:bg-teal-700 text-text-primary border-none" size="sm" onClick={() => handleAsk(expert)}>
                        Ask Them
                      </Button>
                    </div>
                  </div>)}

                <div className="text-center py-2 text-xs text-text-muted">
                  Results based on public stream data. Private streams are not
                  searched.
                </div>
              </div>}

            {/* Step 3: Ask Modal */}
            {step === 'ask' && selectedExpert && <div className="p-6 space-y-4">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-surface-800/50 border border-surface-500">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-text-primary text-xs font-medium">
                    {selectedExpert.name.split(' ').map((n: string) => n[0]).join('')}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-text-primary">
                      To: {selectedExpert.name}
                    </div>
                    <div className="text-xs text-text-secondary">
                      They'll see your question and can respond with AI
                      assistance
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">
                    Your Question
                  </label>
                  <textarea className="w-full bg-surface-800 border border-surface-500 rounded-lg p-3 text-sm text-text-primary placeholder-text-muted focus:outline-none focus:border-teal-500 min-h-[100px]" placeholder="Ask specifically about their experience..." defaultValue={`Hi ${selectedExpert.name.split(' ')[0]}, I saw you worked on ${query}. Can you help me understand...`} />
                </div>

                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">
                    Attach Context
                  </label>
                  <button className="flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-800 border border-surface-500 hover:bg-surface-700 text-xs text-text-primary transition-colors w-full">
                    <Paperclip className="w-3.5 h-3.5" />
                    Add relevant files or streams...
                  </button>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button variant="ghost" className="flex-1" onClick={() => setStep('results')}>
                    Cancel
                  </Button>
                  <Button className="flex-1 bg-teal-600 hover:bg-teal-700 text-text-primary border-none" onClick={onClose}>
                    Send Request
                  </Button>
                </div>
              </div>}
          </motion.div>
        </>}
    </AnimatePresence>;
}